import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, DemoTourProvider } from './contexts';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/AppLayout';
import ErrorBoundary from './components/ErrorBoundary';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';
// Pages
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Settings from './pages/Settings';
import DataImport from './pages/DataImport';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import UserManagement from './pages/UserManagement';
import Patients from './pages/Patients';
import Billing from './pages/Billing';
import Claims from './pages/Claims';
import Performance from './pages/Performance';
import ERAPosting from './pages/ERAPosting';
import PreAuthTracking from './pages/PreAuthTracking';
import PatientStatements from './pages/PatientStatements';
import Documents from './pages/Documents';
import AuditLog from './pages/AuditLog';
import ARaging from './pages/ARaging';
// Dashboard (your original App)
import Dashboard from './App-Dashboard';
// Global session timeout wrapper for HIPAA compliance
const SessionTimeoutWrapper = ({ children }) => {
  const { showWarning, extendSession, handleLogout } = useSessionTimeout();
  return (
    <>
      {children}
      {showWarning && <SessionTimeoutWarning onExtend={extendSession} onTimeout={handleLogout} />}
    </>
  );
};

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <DemoTourProvider>
      <SessionTimeoutWrapper>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<TermsOfService />} /> 
          <Route path="/privacy" element={<PrivacyPolicy />} />
 
          {/* Protected Routes — all wrapped in AppLayout for consistent sidebar */}
          <Route path="/" element={<ProtectedRoute><AppLayout><Dashboard /></AppLayout></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><AppLayout><Settings /></AppLayout></ProtectedRoute>} />
          <Route path="/data-import" element={<ProtectedRoute><AppLayout><DataImport /></AppLayout></ProtectedRoute>} />
          <Route path="/user-management" element={<ProtectedRoute requiredRole="admin"><AppLayout><UserManagement /></AppLayout></ProtectedRoute>} />
          <Route path="/patients" element={<ProtectedRoute><AppLayout><Patients /></AppLayout></ProtectedRoute>} />
          <Route path="/billing" element={<ProtectedRoute><AppLayout><Billing /></AppLayout></ProtectedRoute>} />
          <Route path="/claims" element={<ProtectedRoute><AppLayout><Claims /></AppLayout></ProtectedRoute>} />
          <Route path="/performance" element={<ProtectedRoute><AppLayout><Performance /></AppLayout></ProtectedRoute>} />
          <Route path="/era-posting" element={<ProtectedRoute><AppLayout><ERAPosting /></AppLayout></ProtectedRoute>} />
          <Route path="/preauth" element={<ProtectedRoute><AppLayout><PreAuthTracking /></AppLayout></ProtectedRoute>} />
          <Route path="/statements" element={<ProtectedRoute><AppLayout><PatientStatements /></AppLayout></ProtectedRoute>} />
          <Route path="/documents" element={<ProtectedRoute><AppLayout><Documents /></AppLayout></ProtectedRoute>} />
          <Route path="/audit-log" element={<ProtectedRoute><AppLayout><AuditLog /></AppLayout></ProtectedRoute>} />
          <Route path="/ar-aging" element={<ProtectedRoute><AppLayout><ARaging /></AppLayout></ProtectedRoute>} />

          {/* Redirect unknown routes to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
      </SessionTimeoutWrapper>
      </DemoTourProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;